/* eslint-disable arrow-body-style */

import { LegalSexOptions, YesNoOptions } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import {
  type FormDetails,
  useApplicationContext,
  useCanvasContext,
  useFormContext,
} from "@src/context";
import type { IdValue } from "@src/context/ApplicationContext";
import { useCanvasPortalScrollFix } from "@src/hooks/useCanvasPortalScrollFix";
import { useIdleFormFlush } from "@src/hooks/useIdleFormFlush";
import { useResolvedPageLabels } from "@src/hooks/useResolvedPageLabels";
import { patientInfoFormIds } from "@src/routes/PatientInformation/patientInformationNavigation";
import { useCanvasPrefillSync } from "@src/services/canvas/prefill/useCanvasPrefillSync";
import ClientLogsService from "@src/services/client-logs.service";
import { buildSubmittedDataSnapshot } from "@src/utils/submitted-data-snapshot";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Card } from "@uhg-abyss/web/ui/Card";
import { Link } from "@uhg-abyss/web/ui/Link";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { TextInput } from "@uhg-abyss/web/ui/TextInput";
import {
  Fragment,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FindMemberID } from "./FindMemberIDModal";

interface CareFormFields {
  [key: string]: unknown;
  paymentMethod: string;
  selectedPolicy: string;
  insurancePolicy: IdValue | null;
  insurancePolicies: Array<{ id: string; name: string }>;
  isPrimaryPolicyHolder: boolean | undefined;
  memberId: string;
  gender: string;
}

interface FormErrors {
  paymentMethod?: string;
  selectedPolicy?: string;
  isPrimaryPolicyHolder?: string;
  memberId?: string;
  gender?: string;
}

export const MEMBER_ID_MAX_LENGTH = 25;

export const CoverCareForm = () => {
  const { data, setData } = useApplicationContext();
  const { setHandleSubmit } = useFormContext();
  const { isInSalesforce: canvasIsInSalesforce } = useCanvasContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useCanvasPortalScrollFix(canvasIsInSalesforce);

  const [formValues, setFormValues] = useState<CareFormFields>(() => {
    const initialValues: CareFormFields = {
      paymentMethod: data.paymentMethod || "",
      selectedPolicy: data.selectedPolicy || "",
      insurancePolicy: data.insurancePolicy || null,
      insurancePolicies: data.insurancePolicies || [],
      isPrimaryPolicyHolder:
        typeof data.isPrimaryPolicyHolder === "boolean" ? data.isPrimaryPolicyHolder : undefined,
      memberId: data.memberId || "",
      gender: data.gender || "",
    };

    if (initialValues.paymentMethod === "self_pay") {
      return {
        ...initialValues,
        selectedPolicy: "",
        insurancePolicy: null,
        isPrimaryPolicyHolder: undefined,
        memberId: "",
        gender: "",
      };
    }

    return initialValues;
  });

  const labels = useResolvedPageLabels("coverCareForm");

  const [errors, setErrors] = useState({} as FormErrors);
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);

  const isPrimaryPolicyHolderRef = useRef<HTMLDivElement>(null);
  const memberIdRef = useRef<HTMLInputElement>(null);
  const genderRef = useRef<HTMLDivElement>(null);

  const saveToContext = useCallback(() => {
    const isPrimaryPolicyHolder =
      formValues.paymentMethod === "self_pay" ? false : formValues.isPrimaryPolicyHolder;

    setData("paymentMethod", formValues.paymentMethod || undefined);
    setData("selectedPolicy", formValues.selectedPolicy || undefined);
    setData("insurancePolicy", formValues.insurancePolicy || undefined);
    setData("isPrimaryPolicyHolder", isPrimaryPolicyHolder);
    setData("memberId", formValues.memberId || undefined);
    setData("gender", formValues.gender || undefined);
  }, [formValues, setData]);

  useIdleFormFlush(saveToContext, { flushOnUnmount: true });

  const FormSpacing = useMemo(
    () =>
      styled("div", {
        display: "flex",
        flexDirection: "column",
        gap: "$core.spacing.300",
      }),
    [],
  );

  useCanvasPrefillSync(setFormValues, {
    paymentMethod: (d) => d.paymentMethod,
    selectedPolicy: (d) => d.selectedPolicy,
    insurancePolicy: (d) => d.insurancePolicy,
    insurancePolicies: (d) => d.insurancePolicies,
    isPrimaryPolicyHolder: (d) => d.isPrimaryPolicyHolder,
    memberId: (d) => d.memberId,
    gender: (d) => d.gender,
  });

  const validateField = (fieldName: keyof CareFormFields, value: any): string | undefined => {
    switch (fieldName) {
      case "paymentMethod":
        return value ? undefined : labels.errorPaymentMethod;
      case "selectedPolicy":
        return formValues.paymentMethod !== "self_pay" && !value
          ? "Please enter a valid policy name"
          : undefined;
      case "isPrimaryPolicyHolder":
        return formValues.paymentMethod !== "self_pay" && value === undefined
          ? labels.errorPrimaryPolicyholder
          : undefined;
      case "memberId":
        if (formValues.paymentMethod !== "self_pay" && !value) return labels.errorMemberId;
        if (value && value.length > MEMBER_ID_MAX_LENGTH) {
          return `Member ID must be ${MEMBER_ID_MAX_LENGTH} characters or fewer`;
        }
        return undefined;
      case "gender":
        return formValues.paymentMethod !== "self_pay" && !value ? labels.errorGender : undefined;
      default:
        return undefined;
    }
  };

  const handleFieldChange = (fieldName: keyof CareFormFields, value: any) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [fieldName]: value,
    }));

    if (fieldName === "isPrimaryPolicyHolder" && value === false) {
      setData("isPrimaryPolicyHolder", false);
    }

    if (errors[fieldName]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: undefined,
      }));
    }
  };

  const handleSubmit = async (): Promise<FormDetails> => {
    if (validateAllFields() === false) {
      ClientLogsService.logUserError(
        data.userUuid || "unknown",
        "CoverCareForm.handleSubmit",
        "Form has validation errors, unable to proceed",
      );
      return { isCompleted: false };
    }

    ClientLogsService.logWebAction(
      data.userUuid || "unknown",
      "CoverCareForm.handleSubmit",
      "Form validation passed - proceeding to next patient-information step",
      undefined,
      {},
    );

    setData("paymentMethod", formValues.paymentMethod);
    setData("selectedPolicy", formValues.selectedPolicy);
    setData("insurancePolicy", formValues.insurancePolicy);
    const isPrimaryPolicyHolder =
      formValues.paymentMethod === "self_pay" ? false : formValues.isPrimaryPolicyHolder;

    setData("isPrimaryPolicyHolder", isPrimaryPolicyHolder);
    setData("memberId", formValues.memberId);
    setData("gender", formValues.gender);

    const snapshotBase = buildSubmittedDataSnapshot(data);
    const submittedData = {
      ...snapshotBase,
      paymentMethod: formValues.paymentMethod,
      selectedPolicy: formValues.selectedPolicy,
      insurancePolicy: formValues.insurancePolicy,
      isPrimaryPolicyHolder,
      memberId: formValues.memberId,
      gender: formValues.gender,
    };

    const formInfo: FormDetails = {
      isCompleted: true,
      goToNextSubStep:
        formValues.paymentMethod !== "self_pay" &&
        isPrimaryPolicyHolder !== undefined &&
        !isPrimaryPolicyHolder,
      submittedData,
    };

    setData("coverCareForm", formInfo);

    return formInfo;
  };

  const validateAllFields = (): boolean => {
    const newErrors: FormErrors = {};

    Object.keys(formValues).forEach((key) => {
      const fieldName = key as keyof CareFormFields;
      const error = validateField(fieldName, formValues[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      if (isMobile) {
        if (newErrors.isPrimaryPolicyHolder) {
          isPrimaryPolicyHolderRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        } else if (newErrors.memberId) {
          memberIdRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        } else if (newErrors.gender) {
          genderRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }

      return false;
    }

    return true;
  };

  const handleFormSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  useEffect(() => {
    setHandleSubmit(() => handleSubmit);
  }, [formValues]);

  useEffect(() => {
    if (formValues.paymentMethod === "self_pay") {
      setFormValues((prevValues) => ({
        ...prevValues,
        isPrimaryPolicyHolder: undefined,
      }));
      setData("isPrimaryPolicyHolder", false);
    }
  }, [formValues.paymentMethod, setData]);

  useEffect(() => {
    const hasSeenModal = localStorage.getItem("obc-find-member-id-modal");
    if (hasSeenModal) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  }, []);

  const handleOpenModal = () => {
    setIsModalOpen(true);
    localStorage.setItem("obc-find-member-id-modal", "true");
  };

  let primaryPolicyHolderValue = "";
  if (formValues.isPrimaryPolicyHolder === true) {
    primaryPolicyHolderValue = "yes";
  } else if (formValues.isPrimaryPolicyHolder === false) {
    primaryPolicyHolderValue = "no";
  }

  let paymentMethodDisplayValue = "";
  if (formValues.paymentMethod === "self_pay") {
    paymentMethodDisplayValue = "Self-pay";
  } else if (formValues.paymentMethod === "insurance") {
    paymentMethodDisplayValue = "Insurance";
  }

  return (
    <Fragment>
      {isModalOpen && <FindMemberID isOpen={isModalOpen} setIsOpen={setIsModalOpen} />}
      <Card
        data-testid="test-id-cover-care-card"
        className={isMobile ? "v2card-form-mobile-style" : ""}
        css={{
          width: "100%",
          padding: "2rem",
        }}
      >
        <form
          id={patientInfoFormIds.COVER_CARE}
          data-testid="test-id-cover-care-form"
          onSubmit={handleFormSubmit}
        >
          <FormSpacing>
            <TextInput
              label={labels.labelPaymentMethod}
              data-testid="test-id-cover-care-payment-method-input"
              value={paymentMethodDisplayValue}
              isDisabled
              isRequired
              errorMessage={errors.paymentMethod}
              aria-label={labels.ariaPaymentMethod}
            />
            <TextInput
              label={labels.labelInsurancePolicy}
              data-testid="test-id-cover-care-insurance-policy-input"
              value={
                formValues.selectedPolicy
                  ? formValues.insurancePolicies.find(
                      (policy) => String(policy.id) === String(formValues.selectedPolicy),
                    )?.name || ""
                  : ""
              }
              isDisabled
              isRequired
              errorMessage={errors.selectedPolicy}
              aria-label={labels.ariaInsurancePolicy}
            />
            <SelectInput
              ref={isPrimaryPolicyHolderRef}
              enableOutsideScroll={canvasIsInSalesforce}
              label={labels.labelPrimaryPolicyholder}
              data-testid="test-id-cover-care-primary-policy-holder-select"
              isDisabled={formValues.paymentMethod == "self_pay"}
              value={primaryPolicyHolderValue}
              onChange={(newValue) =>
                handleFieldChange("isPrimaryPolicyHolder", String(newValue).toLowerCase() === "yes")
              }
              isRequired
              errorMessage={errors.isPrimaryPolicyHolder}
              options={YesNoOptions}
              aria-label={labels.ariaPrimaryPolicyholder}
            />
            <div>
              <TextInput
                ref={memberIdRef}
                label={labels.labelMemberId}
                data-testid="test-id-cover-care-member-id-input"
                value={formValues.memberId}
                onChange={(newValue) => {
                  const filtered = newValue.target.value.replace(/[^a-zA-Z0-9]/g, "");
                  handleFieldChange("memberId", filtered);
                }}
                isDisabled={formValues.paymentMethod == "self_pay"}
                isRequired
                errorMessage={errors.memberId}
                aria-label={labels.ariaMemberId}
              />
              <Link
                data-testid="test-id-cover-care-find-member-id-link"
                onClick={handleOpenModal}
                id="obc-cover-care-find-member-id-link"
                data-obc-label="Cover Care Find Member ID Link"
                aria-label="Sample Image link to help find Member ID"
                css={{
                  "abyss-link-root": {
                    alignSelf: "flex-start",
                    textDecoration: "underline",
                    color: "$app.color.neutral.dark",
                    fontWeight: "400",
                  },
                }}
              >
                {labels.linkFindMemberId}
              </Link>
            </div>
            <SelectInput
              ref={genderRef}
              enableOutsideScroll={canvasIsInSalesforce}
              label={labels.labelGender}
              data-testid="test-id-cover-care-gender-select"
              isDisabled={formValues.paymentMethod == "self_pay"}
              value={formValues.gender}
              onChange={(newValue) => handleFieldChange("gender", String(newValue))}
              isRequired
              errorMessage={errors.gender}
              options={LegalSexOptions}
              aria-label={labels.ariaGender}
            />
          </FormSpacing>
        </form>
      </Card>
    </Fragment>
  );
};
